////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////

import React, {useReducer} from 'react';
import Selector from "./Selector";
import {FileDestinationNamingType} from "../clients/Classes";
import {DefaultItem} from "@adsk/alloy-react-dropdown";
import {
  CellDescription,
  CellSelectors,
  ColumnLabels,
  ColumnValues,
  FlexColumn,
  FlexRow,
  FlexRowCentered,
  RadioLabel,
  RadioSpan,
  RequireToken,
  RowSettings
} from "../CommonStyledComponents";
import Theme from "@adsk/alloy-react-theme";
import {CheckboxState} from "@adsk/alloy-react-checkbox";
import {InfoPopover} from "@adsk/alloy-react-tooltip";
import {OptionsState} from "./states/OptionsState";
import {reducer} from "./reducers/OptionsReducer";
import {OptionsActions, TaskTrigger} from "../Enums";
import TextInput from "@adsk/alloy-react-text-input";
import Divider from "@adsk/alloy-react-divider";
import ToggleWithLabel from "./ToggleWithLabel";
import {RadioButton, RadioGroup} from "@adsk/alloy-react-radio-button";
import {RecurrenceSettings} from "../dataModel/RecurrenceSettings";
import SettingsTrigger from "./SettingsTrigger";

const SettingsOptions = (
  {
    name,
    description,
    namingType,
    emailOnCompletion,
    attachCsv,
    maintainFolderStructure,
    retainCloudModel,
    renameExisting,
    recurrenceSettings,
    taskTrigger,
    startTime,
    allowRecurring,
    onChanged,
    onTriggerChanged,
  }: {
    name: string | undefined,
    description: string | undefined,
    namingType: FileDestinationNamingType | undefined,
    emailOnCompletion: boolean,
    attachCsv: boolean,
    maintainFolderStructure: boolean,
    retainCloudModel: boolean,
    renameExisting: boolean,
    recurrenceSettings: RecurrenceSettings | undefined,
    taskTrigger: TaskTrigger | undefined,
    startTime: Date | undefined,
    allowRecurring: boolean,
    onChanged?: (property: keyof OptionsState, value: OptionsState[keyof OptionsState]) => void,
    onTriggerChanged?: (newTrigger: TaskTrigger | undefined, newStartDate?: Date | undefined, newRecurrence?: RecurrenceSettings | undefined) => void,
  }) => {
  const [state, dispatch] = useReducer(reducer, new OptionsState(
    name,
    description,
    taskTrigger,
    recurrenceSettings,
    startTime,
    namingType,
    emailOnCompletion,
    attachCsv,
    maintainFolderStructure,
    retainCloudModel,
    renameExisting,
    allowRecurring
  ));

  const fileNamingOptions: DefaultItem[] = [
    {value: FileDestinationNamingType.Overwrite, label: 'Overwrite Existing Files'},
    {value: FileDestinationNamingType.AppendTimestamp, label: 'Append Time Stamp'},
  ];

  function onStringChanged(property: keyof OptionsState, value: string) {
    let action: OptionsActions;
    switch (property) {
      case 'name':
        action = OptionsActions.name;
        break;
      case 'description':
        action = OptionsActions.description;
        break;
      default:
        throw new Error(`Unrecognized property "${property}"`);
    }

    dispatch({type: action, payload: value});
    if (onChanged) {
      onChanged(property, value);
    }
  }

  function onCheckboxChanged(property: keyof OptionsState, value: CheckboxState) {
    const newVal = value === true;

    let action: OptionsActions;
    switch (property) {
      case 'email':
        action = OptionsActions.email;
        break;
      case 'exportCsv':
        action = OptionsActions.exportCsv;
        break;
      case 'maintainFolderStructure':
        action = OptionsActions.maintainFolderStructure;
        break;
      case 'retainCloudModel':
        action = OptionsActions.retainCloudModel;
        break;
      case 'renameExisting':
        action = OptionsActions.renameExisting;
        break;
      default:
        throw new Error(`Unrecognized property "${property}"`);
    }

    dispatch({type: action, payload: newVal});
    if (onChanged) {
      onChanged(property, newVal);
    }
  }

  function onDropdownChanged(property: keyof OptionsState, value: DefaultItem | null | undefined): void {
    let newVal: OptionsState['naming'];
    let action: OptionsActions;

    switch (property) {
      case "naming":
        newVal = value == null
          ? FileDestinationNamingType.None
          : value.value as FileDestinationNamingType;
        action = OptionsActions.naming;
        break;
      default:
        throw new Error(`Unrecognized property "${property}"`);
    }

    dispatch({type: action, payload: newVal});
    if (onChanged) {
      onChanged(property, newVal);
    }
  }

  function passThroughChange(property: keyof OptionsState, value: OptionsState[keyof OptionsState]): void {
    if (onChanged) {
      onChanged(property, value);
    }
  }

  return (
    <div>
      <RowSettings>
        <ColumnLabels>
          <h2 style={Theme.typography.heading2}>General</h2>
        </ColumnLabels>
        <ColumnValues>
          <CellDescription>
            <FlexColumn>
              <h3 style={Theme.typography.heading3}>Name<RequireToken>*</RequireToken></h3>
              <TextInput value={state.name}
                         placeholder={'Enter a task name...'}
                         style={{marginBottom: '30px'}}
                         onChange={v => onStringChanged('name', v.target.value)}/>
              <h3 style={Theme.typography.heading3}>Description</h3>
              <TextInput value={state.description}
                         placeholder={'Enter a task description (optional)...'}
                         onChange={v => onStringChanged('description', v.target.value)}/>
            </FlexColumn>
          </CellDescription>
        </ColumnValues>
      </RowSettings>
      <Divider/>
      <RowSettings>
        <ColumnLabels>
          <h2 style={Theme.typography.heading2}>Trigger</h2>
        </ColumnLabels>
        <ColumnValues style={{marginTop: '1rem'}}>
          <SettingsTrigger
            recurrenceSettings={state.schedule}
            taskTrigger={state.trigger}
            startTime={state.startDate}
            allowRecurring={allowRecurring}
            onTriggerChanged={onTriggerChanged}
            onScheduleChanged={s => passThroughChange('schedule', s)}
            onStartTimeChanged={d => passThroughChange('startDate', d)}/>
        </ColumnValues>
      </RowSettings>
      <Divider/>
      <RowSettings>
        <ColumnLabels>
          <h2 style={Theme.typography.heading2}>Files &amp; Folders</h2>
        </ColumnLabels>
        <ColumnValues>
          <FlexRowCentered style={{paddingTop: '1rem'}}>
            <CellDescription>Maintain folder structure at destination</CellDescription>
            <CellSelectors>
              <ToggleWithLabel
                checked={state.maintainFolderStructure}
                style={{marginLeft: '20px'}}
                onChange={v => onCheckboxChanged('maintainFolderStructure', v)}/>
            </CellSelectors>
          </FlexRowCentered>
          <FlexRow>
            <CellDescription>
              <FlexColumn>
                <h3 style={Theme.typography.heading3}>File naming</h3>
              </FlexColumn>
              <Selector items={fileNamingOptions}
                        selected={fileNamingOptions.find(o => o.value === state.naming)}
                        onSelectionChange={x => onDropdownChanged('naming', x)}/>
            </CellDescription>
          </FlexRow>
        </ColumnValues>
      </RowSettings>
      <Divider/>
      <RowSettings>
        <ColumnLabels>
          <h2 style={Theme.typography.heading2}>Revit Cloud Models</h2>
        </ColumnLabels>
        <ColumnValues>
          <FlexRow>
            <CellDescription>
              <h3 style={Theme.typography.heading3}>Behavior</h3>
              <RadioGroup name={'cloudModel'}
                          value={state.retainCloudModel ? 'retain' : 'remove'}
                          onChange={v => onCheckboxChanged('retainCloudModel', v === 'retain')}>
                <RadioLabel>
                  <RadioButton value="retain" style={{flexShrink: 0}}/>
                  <RadioSpan>Retain the Cloud Model at the destination</RadioSpan>
                </RadioLabel>
                <RadioLabel>
                  <RadioButton value="remove" style={{flexShrink: 0}}/>
                  <RadioSpan>Only copy over the Revit file and don't keep it as a Cloud Model</RadioSpan>
                </RadioLabel>
              </RadioGroup>
            </CellDescription>
          </FlexRow>
          <FlexRow style={{marginTop: '30px'}}>
            <CellDescription>
              <FlexRowCentered>
                <h3 style={Theme.typography.heading3}>Duplicates</h3>
                <InfoPopover style={{marginLeft: '1rem'}}
                             message={(
                               <>
                                 <p>You cannot overwrite Revit Cloud Models.</p>
                                 <p>When copying Revit Cloud Models and a Cloud Model of the same name already exists in
                                   the destination, this option will allow you to choose which Cloud Model will be
                                   renamed with a date/time stamp appended to the end of the name.</p>
                               </>
                             )}/>
              </FlexRowCentered>
              <RadioGroup name={'duplicates'}
                          value={state.renameExisting ? 'existing' : 'new'}
                          onChange={v => onCheckboxChanged('renameExisting', v === 'existing')}>
                <RadioLabel>
                  <RadioButton value="existing"
                               style={{flexShrink: 0}}
                               disabled={!state.retainCloudModel}/>
                  <RadioSpan>Rename the existing model by appending a date/time stamp to the name</RadioSpan>
                </RadioLabel>
                <RadioLabel>
                  <RadioButton value="new"
                               style={{flexShrink: 0}}
                               disabled={!state.retainCloudModel}/>
                  <RadioSpan>Rename the newly copied model</RadioSpan>
                </RadioLabel>
              </RadioGroup>
            </CellDescription>
          </FlexRow>
        </ColumnValues>
      </RowSettings>
      <Divider/>
      <RowSettings>
        <ColumnLabels>
          <h2 style={Theme.typography.heading2}>Transfer log</h2>
        </ColumnLabels>
        <ColumnValues>
          <FlexRowCentered style={{paddingTop: '1rem'}}>
            <CellDescription>Email me when the task completes</CellDescription>
            <CellSelectors>
              <ToggleWithLabel
                checked={state.email}
                style={{marginLeft: '20px'}}
                onChange={v => onCheckboxChanged('email', v)}/>
            </CellSelectors>
          </FlexRowCentered>
          <FlexRowCentered style={{marginTop: '16px'}}>
            <CellDescription>Attach the CSV transfer log</CellDescription>
            <CellSelectors>
              <ToggleWithLabel
                disabled={!state.email}
                checked={state.exportCsv}
                style={{marginLeft: '20px'}}
                onChange={v => onCheckboxChanged('exportCsv', v)}/>
            </CellSelectors>
          </FlexRowCentered>
        </ColumnValues>
      </RowSettings>
    </div>
  );
};

export default SettingsOptions;